import { useCallback, useEffect, useRef, useState } from "react";
import { ClickAwayListener, MenuItem, MenuList, Paper, Popper } from "@mui/material";
import { models } from "types/api/viewModels.ts";
import { NavigationActionType } from "types/declarations/enums.ts";
import * as Styles from "./topNavigationBar.styles";

type NavigationItem = models["NavigationItemViewModel"];
type NavigationMenu = models["NavigationMenuViewModel"] & { items: NavigationItem[] };

interface DesktopNavigationMenusProps {
	menus: NavigationMenu[];
	isCurrentSection: (menu: NavigationMenu) => boolean;
	onNavigate: (item: NavigationItem) => void;
}

interface OpenMenu {
	id: string;
	anchor: HTMLButtonElement;
	items: NavigationItem[];
	autoFocus: boolean;
}

const DesktopNavigationMenus = ({ menus, isCurrentSection, onNavigate }: DesktopNavigationMenusProps) => {
	const [openMenu, setOpenMenu] = useState<OpenMenu | null>(null);
	const menuRef = useRef<HTMLUListElement>(null);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const keyboardInteraction = useRef(false);
	const lastInputWasKeyboard = useRef(false);

	const cancelClose = useCallback(() => {
		if (closeTimer.current !== null) {
			clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
	}, []);

	useEffect(() => cancelClose, [cancelClose]);

	const closeMenu = useCallback(() => {
		cancelClose();
		if (menuRef.current?.contains(document.activeElement)) openMenu?.anchor.focus();
		keyboardInteraction.current = false;
		setOpenMenu(null);
	}, [cancelClose, openMenu]);

	useEffect(() => {
		if (!openMenu) return;

		const handleKeyDown = (event: KeyboardEvent) => {
			lastInputWasKeyboard.current = true;
			if (event.key === "Escape") {
				event.preventDefault();
				closeMenu();
				return;
			}
			if (menuRef.current?.contains(document.activeElement) || document.activeElement === openMenu.anchor) {
				keyboardInteraction.current = true;
				cancelClose();
			}
		};
		const handlePointerDown = () => {
			lastInputWasKeyboard.current = false;
			keyboardInteraction.current = false;
		};

		document.addEventListener("keydown", handleKeyDown, true);
		document.addEventListener("pointerdown", handlePointerDown, true);
		return () => {
			document.removeEventListener("keydown", handleKeyDown, true);
			document.removeEventListener("pointerdown", handlePointerDown, true);
		};
	}, [cancelClose, closeMenu, openMenu]);

	const scheduleClose = () => {
		cancelClose();
		if (keyboardInteraction.current) return;
		closeTimer.current = setTimeout(closeMenu, 150);
	};

	const showMenu = (anchor: HTMLButtonElement, id: string, items: NavigationItem[], activation?: boolean) => {
		cancelClose();
		if (openMenu?.id !== id) {
			if (menuRef.current?.contains(document.activeElement)) openMenu?.anchor.focus();
			keyboardInteraction.current = false;
		}
		if (activation !== undefined) {
			keyboardInteraction.current = activation;
			lastInputWasKeyboard.current = activation;
			if (openMenu?.id === id) menuRef.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus();
		}
		setOpenMenu({ id, anchor, items, autoFocus: activation !== undefined });
	};

	return (
		<ClickAwayListener onClickAway={closeMenu}>
			<Styles.NavigationMenus>
				{menus.map((menu) => {
					const items = menu.items.filter(
						(item) => item.title && (item.path || item.actionType === NavigationActionType.LOGOUT)
					);
					const primaryItem = items[0];
					if (!primaryItem) return null;
					const menuId = menu.id ?? primaryItem.id ?? primaryItem.title ?? "navigation-menu";
					const isSelected = isCurrentSection(menu);

					return (
						<Styles.NavigationMenuButton
							key={menuId}
							id={`${menuId}-button`}
							isSelected={isSelected}
							onPointerEnter={(event) => {
								if (event.pointerType === "mouse") showMenu(event.currentTarget, menuId, items);
							}}
							onPointerLeave={(event) => {
								if (event.pointerType === "mouse") scheduleClose();
							}}
							onClick={(event) => showMenu(event.currentTarget, menuId, items, event.detail === 0)}
							aria-controls={openMenu?.id === menuId ? `${menuId}-menu` : undefined}
							aria-current={isSelected ? "page" : undefined}
							aria-expanded={openMenu?.id === menuId}
							aria-haspopup="menu"
						>
							<Styles.NavigationButtonLabel>{menu.title}</Styles.NavigationButtonLabel>
						</Styles.NavigationMenuButton>
					);
				})}
				<Popper
					open={Boolean(openMenu)}
					anchorEl={openMenu?.anchor}
					placement="bottom-start"
					sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}
				>
					{openMenu && (
						<Paper
							elevation={8}
							sx={{ maxHeight: "calc(100vh - 96px)", overflowY: "auto" }}
							onPointerEnter={(event) => {
								if (event.pointerType === "mouse") cancelClose();
							}}
							onPointerLeave={(event) => {
								if (event.pointerType === "mouse") scheduleClose();
							}}
						>
							<MenuList
								key={openMenu.id}
								ref={menuRef}
								id={`${openMenu.id}-menu`}
								aria-labelledby={`${openMenu.id}-button`}
								autoFocusItem={openMenu.autoFocus}
								onFocus={() => {
									if (lastInputWasKeyboard.current) {
										keyboardInteraction.current = true;
										cancelClose();
									}
								}}
								onBlur={(event) => {
									if (
										!event.currentTarget.contains(event.relatedTarget) &&
										event.relatedTarget !== openMenu.anchor
									) {
										closeMenu();
									}
								}}
								onKeyDown={(event) => {
									if (event.key === "Tab") {
										event.preventDefault();
										closeMenu();
									}
								}}
							>
								{openMenu.items.map((item) => (
									<MenuItem
										key={item.id ?? item.title}
										onClick={() => {
											closeMenu();
											onNavigate(item);
										}}
									>
										{item.title}
									</MenuItem>
								))}
							</MenuList>
						</Paper>
					)}
				</Popper>
			</Styles.NavigationMenus>
		</ClickAwayListener>
	);
};

export default DesktopNavigationMenus;
